import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, DublettenTreffer, Unternehmen, PartnerRole } from "@kundeninfo/api-client";
import { Feld, darfAbbrechen, useFormularZustand, useBestaetigung, useSofortPopup } from "@kundeninfo/ui";
import { Chip, Hinweis } from "@kundeninfo/ui/me-design";
import type { VerlassenWaechter } from "../router.js";
import { hausfarbeChip } from "../markenfarben.js";
import { KiKnopf } from "../components/meHuellen.js";
import { LOGO_TEXT, PARTNER_TEXT } from "./partners/texts.js";
import { BREIT, EMPTY_DRAFT, GeschaeftspartnerDialog, MIN_NAME_LENGTH, PARTNER_FIELDS, PARTNER_LABELS, changedFields,
  masterFrom, orNull, partnerFieldId, usePartnerLeaveGuard } from "./partners/GeschaeftspartnerDialog.js";
import type { PartnerDraft } from "./partners/GeschaeftspartnerDialog.js";
import { RoleChoiceField, hasRole, roleChoiceFrom, rolesOf } from "./partners/RoleChoice.js";
import type { RoleChoice } from "./partners/RoleChoice.js";
import { ROLLEN } from "./partners/rollenFilter.js";
import "./KundeAnlegen.css";

/** Nur die Klientenmethoden, die diese Maske wirklich braucht — so lässt sie sich im Test
 *  mit wenigen vi.fn zeichnen, ohne den ganzen ApiClient nachzubauen. lookupEnrichment und
 *  uploadCustomerLogo kamen mit der Anreicherung dazu (Aufgabe 7). */
export type AnlegenClient =
  Pick<ApiClient, "findDuplicates" | "createCustomer"
    | "lookupEnrichment" | "uploadCustomerLogo"
    // „Rolle Kunde hinzufügen“ am Dublettentreffer (Lieferanten Strang A): Beziehung lesen, Rollen setzen bzw. anlegen.
    | "getCustomer" | "setRelationRoles" | "addRelation"
    // Die Ortszeile (Spec „Adresszeile", R17): Länderkürzel und PLZ-Verzeichnis.
    | "listLaender" | "listPlzOrte">;

export type KundeAnlegenProps = {
  client: AnlegenClient;
  /** Die sichtbaren Unternehmen (sitzung.companies) — für die Kürzel-Chips der Treffer, den
   *  Namen im Zwischentitel und das Ziel von „Zu <Kürzel> wechseln". */
  companies: Unternehmen[];
  /** Das aktive Unternehmen — hier entsteht der Kunde (E361). */
  arbeitsunternehmenId: string | null;
  /** Nach dem Anlegen oder bei „Zur Kundenakte" am Treffer: die Akte dieses Kunden öffnen. */
  onFertig(id: string): void;
  /** „Zu <Kürzel> wechseln" am Dublettentreffer (E361, R20): App wechselt das aktive Unternehmen
   *  und öffnet nach dem Neuladen die Akte des Treffers im Reiter Stammdaten. `ungefragt`: Die Maske
   *  trägt nur den Suchnamen, es geht nichts verloren — App fragt dann keinen Wächter und gibt den
   *  Browser-Schutz erst unmittelbar vor dem Neuladen frei. */
  onWechseln(companyId: string, kundeId: string, o: { ungefragt: boolean }): void;
  /**
   * Kreuz, Escape und „Abbrechen“ (Entscheid 524, Hausregel „Modal-Bearbeitung“): Der Aufrufer hängt das Panel aus. Mit
   * Eingaben ist die Rückfrage („Änderungen verwerfen?“) vorher beantwortet, der Verlassen-Wächter abgemeldet — die
   * Navigation danach fragt kein zweites Mal.
   */
  onAbbrechen(): void;
  registriereVerlassenWaechter(frage: VerlassenWaechter): () => void;
  /** Die aktiven Rollen-Chips der Liste — Vorgabe der Rollenwahl (C9); ohne Angabe alle (= Bestandsvorgabe). */
  rollenVorgabe?: readonly PartnerRole[];
};

/** Entprellung der Dublettensuche — ohne sie eine Abfrage je Tastendruck. */
const SUCH_VERZOEGERUNG_MS = 300;

/** Die Rollen eines Dublettentreffers in Worten (Kennzeichen-Schlüssel, P12). */
const ROLLEN_TEXT: Record<PartnerRole, string> = {
  CUSTOMER: PARTNER_TEXT.roleCustomer, PROSPECT: PARTNER_TEXT.roleProspect, SUPPLIER: PARTNER_TEXT.roleSupplier,
};

/** Alle Feldnamen, die eine 422 nennen kann: die Stammdaten des gemeinsamen Dialogs und die Beziehungswerte; `status`
 *  und `supplierStatus` (ROLLE_FEHLT) stehen an der Rollenwahl. */
const EINGABE_NAMEN: string[] = [
  ...PARTNER_FIELDS.map((f) => f.name), "customerNumber", "status", "supplierStatus", "customerSince",
];
const BESCHRIFTUNGEN: Record<string, string> = {
  ...PARTNER_LABELS, customerNumber: PARTNER_TEXT.customerNumber, customerSince: PARTNER_TEXT.customerSince,
};

function ortVon(t: DublettenTreffer): string {
  return [t.postalCode, t.city]
    .filter((teil): teil is string => teil !== null && teil !== "")
    .join(" ");
}

/**
 * „Neuer Geschäftspartner“ — der gemeinsame Geschäftspartner-Dialog im Modus `anlegen` (Entscheide 814–819) über der
 * aktuellen Ansicht; die Adresse /kunden/neu öffnet ihn als Direktaufruf. Nur hier (819): ZUERST der Firmenname — ab drei
 * Zeichen sucht die Maske (entprellt) über ALLE Unternehmen nach bestehenden Firmen und lenkt bei einem Treffer in einem
 * anderen Unternehmen zum Wechsel dorthin (Mandantentrennung E361, R20) statt auf eine zweite Firmenzeile. Verboten wird
 * nichts: Wer sicher ist, dass es eine andere Firma ist, legt trotzdem an. Der Partner entsteht immer im aktiven
 * Unternehmen; am Ende steht der Block „Beziehung bei …“.
 *
 * Das Nummernfeld bleibt leer und trägt nur den Hinweis, dass die Nummer beim Speichern vergeben wird — KEIN Vorschlag
 * beim Öffnen: Zwei gleichzeitig offene Formulare bekämen denselben, und jedes abgebrochene Formular verbrännte eine
 * Nummer. Eine bestehende Nummer (Portal-Mandant, später DATEV-Debitor) wird von Hand eingetragen.
 */
export function KundeAnlegen(p: KundeAnlegenProps): ReactElement {
  const [entwurf, setEntwurf] = useState<PartnerDraft>(EMPTY_DRAFT);
  const [nummer, setNummer] = useState("");
  const [startRollen] = useState<RoleChoice>(() => roleChoiceFrom(p.rollenVorgabe ?? ROLLEN));
  const [rollen, setRollen] = useState<RoleChoice>(startRollen);
  const [seit, setSeit] = useState("");
  /** Nummer und „Kunde seit“ gehören zur Kundenrolle — ohne sie stehen die Felder nicht da und gehen nicht mit. */
  const mitKundenrolle = rollen.customer !== null;
  const rolleGewaehlt = hasRole(rollen);

  const [treffer, setTreffer] = useState<DublettenTreffer[]>([]);
  /** Nur noch der Hinweis „angelegt, aber das Logo nicht“ und „Rolle Kunde hinzufügen“ — Befunde des Anlegens selbst
   *  führt `formular.meldung`. */
  const [meldung, setMeldung] = useState<string | null>(null);
  /** Der Schreibweg, den `formular.aktion()` gleich ausführt — gesetzt beim Klick, damit er
   *  den Entwurf DIESES Zeichnens liest. */
  const auftrag = useRef<() => Promise<{ id: string; logoFehler: string | null }>>(
    async () => ({ id: "", logoFehler: null }));
  /** Das im Markenrechts-Dialog bestätigte Logo — erst NACH dem Anlegen an die Logo-Route gereicht (817). */
  const [gewaehltesLogo, setGewaehltesLogo] = useState<string | null>(null);
  /** Gesetzt, wenn der Partner ANGELEGT ist, das gewählte Logo aber scheiterte: Dann wird NICHT automatisch navigiert
   *  (sonst sähe niemand die Meldung), sondern der Bearbeiter springt selbst mit „Zur Kundenakte" in die Akte. */
  const [angelegtId, setAngelegtId] = useState<string | null>(null);

  // Die Dublettensuche: entprellt, und der laufende Timer wird bei jeder weiteren
  // Eingabe wie beim Verlassen der Maske aufgeräumt (die Effekt-Aufräumfunktion läuft
  // in beiden Fällen). `abgebrochen` fängt zusätzlich eine schon abgeschickte Antwort
  // ab, die erst nach dem Verlassen einträfe.
  const suchName = entwurf.companyName.trim();
  const suche = p.client.findDuplicates;
  useEffect(() => {
    if (suchName.length < MIN_NAME_LENGTH) { setTreffer([]); return; }
    let abgebrochen = false;
    const timer = setTimeout(() => {
      suche(suchName)
        .then((antwort) => { if (!abgebrochen) setTreffer(antwort.items); })
        // Die Suche ist Lenkung, kein Pflichtweg: Scheitert sie (Netz weg), bleibt die
        // Maske still bedienbar, statt das Anlegen mit einer Fehlermeldung zu verstellen.
        .catch(() => {});
    }, SUCH_VERZOEGERUNG_MS);
    return () => { abgebrochen = true; clearTimeout(timer); };
  }, [suchName, suche]);

  // Verlassen-Wächter: registriert, sobald irgendeine Eingabe von der leeren Maske abweicht. `weitereEingaben`: alles
  // außer dem Firmennamen — der allein ist der Suchbegriff der Dublettenlenkung, an ihm geht beim Sprung zu einem Treffer
  // nichts verloren.
  const weitereEingaben = changedFields(entwurf, EMPTY_DRAFT).some((f) => f !== "companyName")
    || nummer !== "" || seit !== ""
    || rollen.customer !== startRollen.customer || rollen.supplier !== startRollen.supplier;
  const geaendert = entwurf.companyName !== "" || weitereEingaben;
  const abmeldenRef = usePartnerLeaveGuard(geaendert, p.registriereVerlassenWaechter);
  const { bestaetigen } = useBestaetigung();

  /**
   * Formularzustände (ME-Etappe 3, Aufgabe 3): Während des Anlegens sind alle Felder gesperrt (readOnly +
   * aria-disabled, der Fokus bleibt) und der Knopf steht; eine 422 hängt am genannten Feld — auch an der Rollenwahl —
   * und der Fokus springt dorthin; ein Befund zu einem Feld, das diese Maske nicht zeigt, steht in der Meldung (nie
   * unsichtbar). Anlegen kennt keine Version, also keine 409.
   */
  const formular = useFormularZustand({
    speichern: () => auftrag.current(),
    neuLaden: async () => { /* keine 409 beim Anlegen */ },
    bekannteFelder: EINGABE_NAMEN,
    beschriftungen: BESCHRIFTUNGEN,
  });
  const arbeitet = formular.laeuft;
  const feldFehler = formular.feldFehler as Partial<Record<string, string>>;

  /** Der Erfolgsweg nach dem Anlegen: Wächter abmelden, dann in die Akte springen. */
  function fertig(id: string): void {
    abmeldenRef.current?.();
    p.onFertig(id);
  }

  /** Der Sprung in die Akte eines Dublettentreffers: Mit dem Suchnamen allein geht nichts verloren — dann
   *  ohne Frage (der Wächter meldet sich ab); sonst fragt er bei der Navigation. */
  function zurAkteDesTreffers(id: string): void {
    if (!weitereEingaben) abmeldenRef.current?.();
    p.onFertig(id);
  }

  /**
   * Der eine Weg hinaus neben dem Anlegen (Kreuz, Escape, „Abbrechen“): ohne Eingaben sofort, mit Eingaben nach der
   * gestapelten Rückfrage. Der Verlassen-Wächter wird vorher abgemeldet, sonst fragte die Navigation des Aufrufers
   * (Direktaufruf → Kundenliste) ein zweites Mal. Steht der Partner schon (nur das Logo scheiterte), geht nichts
   * verloren: Der Weg hinaus führt in seine Akte, wie der Knopf „Zur Kundenakte“. Solange das Anlegen läuft, kommt
   * nichts hierher: Kreuz und Escape sperrt `abbrechenGesperrt` am Dialog, den Knopf `deaktiviert`.
   */
  async function abbrechen(): Promise<void> {
    if (angelegtId !== null) { fertig(angelegtId); return; }
    if (!(await darfAbbrechen(geaendert))) return;
    abmeldenRef.current?.();
    p.onAbbrechen();
  }

  async function anlegen(): Promise<void> {
    if (!rolleGewaehlt) return;
    setMeldung(null);
    auftrag.current = async () => {
      // Kein companyId: Der Server nimmt das aktive Unternehmen der Anfrage (R7). Die Beziehungswerte: die Rollen (P2)
      // und — nur mit Kundenrolle — Nummer und Datum.
      const antwort = await p.client.createCustomer({ customer: masterFrom(entwurf),
        customerNumber: mitKundenrolle ? orNull(nummer) : null, ...rolesOf(rollen),
        customerSince: mitKundenrolle ? orNull(seit) : null });
      // Reihenfolge Anlegen → Logo: die Logo-Route braucht die frisch vergebene Kunden-Id. Scheitert das Logo, BLEIBT
      // der Partner bestehen (kein Rollback) — das ist KEIN Fehler des Anlegens, sondern ein Ergebnis mit Grund.
      if (gewaehltesLogo !== null) {
        try {
          await p.client.uploadCustomerLogo(antwort.id, gewaehltesLogo);
        } catch (logoFehler) {
          return { id: antwort.id, logoFehler: logoFehler instanceof ApiError ? logoFehler.message : LOGO_TEXT.unknownError };
        }
      }
      return { id: antwort.id, logoFehler: null };
    };
    const ergebnis = await formular.aktion();
    if (ergebnis === undefined) return; // Befund am Feld oder in der Meldung
    if (ergebnis.logoFehler !== null) {
      // NICHT automatisch navigieren — die Navigation hängt die Maske aus, und die Meldung wäre nie zu sehen
      // (Abschlussfund W1). Die Maske bleibt stehen, mit Meldung und dem ausdrücklichen Knopf „Zur Kundenakte".
      setMeldung(PARTNER_TEXT.createdWithoutLogo(ergebnis.logoFehler));
      setAngelegtId(ergebnis.id);
      return;
    }
    fertig(ergebnis.id);
  }

  /** Das aktive Unternehmen — hier entsteht der Partner; an ihm misst sich, ob ein Dubletten-Treffer schon „hier" Kunde
   *  ist (siehe Trefferliste). */
  const aktiv = p.companies.find((c) => c.id === p.arbeitsunternehmenId);

  /** „Anlegen“ ohne jede Rolle ist gesperrt; das Sofort-Popup sagt warum (Hausregel „Sofort-Popup statt title“). */
  const anlegenHinweis = useSofortPopup<HTMLElement>(rolleGewaehlt ? "" : PARTNER_TEXT.atLeastOne);
  /** Läuft gerade „Rolle Kunde hinzufügen“ — der Riegel gegen einen zweiten Klick. */
  const [hinzuLaeuft, setHinzuLaeuft] = useState(false);

  /**
   * „Rolle Kunde hinzufügen“ am Treffer (Spec „Wortwahl“, Dublettenhinweis): Die Firma gibt es schon — statt einer
   * zweiten Firmenzeile bekommt sie die Kundenrolle im aktiven Unternehmen. Besteht dort eine Beziehung (etwa als
   * Lieferant), setzt der Rollenweg die Kundenrolle dazu; sonst entsteht die Beziehung als Kunde. Erst nach der
   * gestapelten Bestätigung, danach in die Akte.
   */
  async function kundeHinzufuegen(t: DublettenTreffer): Promise<void> {
    if (aktiv === undefined || hinzuLaeuft) return;
    if (!(await bestaetigen({ titel: PARTNER_TEXT.confirmAddCustomerRole(t.companyName, aktiv.shortCode),
      bestaetigenText: PARTNER_TEXT.add }))) return;
    setMeldung(null);
    setHinzuLaeuft(true);
    try {
      if (t.relationRoles.some((r) => r.shortCode === aktiv.shortCode)) {
        const akte = await p.client.getCustomer(t.id);
        const r = akte.relations.find((x) => x.companyId === aktiv.id);
        if (r === undefined) throw new Error(PARTNER_TEXT.addCustomerRoleFailed);
        await p.client.setRelationRoles(t.id, r.id, { version: r.version, status: "ACTIVE", supplierStatus: r.supplierStatus });
      } else {
        await p.client.addRelation(t.id, { companyId: aktiv.id, customerNumber: null, status: "ACTIVE",
          supplierStatus: null, customerSince: null });
      }
      zurAkteDesTreffers(t.id);
    } catch (fehler) {
      setMeldung(fehler instanceof Error && fehler.message !== "" ? fehler.message : PARTNER_TEXT.addCustomerRoleFailed);
    } finally {
      setHinzuLaeuft(false);
    }
  }

  /** Die Dublettenlenkung (819, nur beim Anlegen) — als Hinweiskasten, nicht als Fehler: Sie lenkt, sie verbietet nicht. */
  const dubletten = treffer.length === 0 ? null : (
    <section className="ka__dubletten" aria-label={PARTNER_TEXT.duplicatesRegion} {...BREIT}>
      <h3 className="ka__dubletten-titel">{PARTNER_TEXT.duplicatesTitle}</h3>
      <p className="ka__dubletten-text">{PARTNER_TEXT.duplicatesText}</p>
      <ul className="ka__dubletten-liste">
        {treffer.map((t) => {
          // Die Rollen des Treffers je Kürzel (P12) — Nummern und Status bleiben geheim.
          const rollenText = t.relationRoles.map((r) => PARTNER_TEXT.rolesAt(
            r.roles.length === 0 ? [PARTNER_TEXT.noCurrentRole] : r.roles.map((x) => ROLLEN_TEXT[x]), r.shortCode)).join(", ");
          const hier = aktiv === undefined ? undefined : t.relationRoles.find((r) => r.shortCode === aktiv.shortCode);
          const schonKunde = hier !== undefined && hier.roles.some((x) => x !== "SUPPLIER");
          return (
            <li key={t.id} className="ka__dublette">
              <span className="ka__dublette-name">{PARTNER_TEXT.duplicate(t.companyName, rollenText)}</span>
              {ortVon(t) !== "" && <span className="ka__dublette-ort">{ortVon(t)}</span>}
              {/* Absichtlich nur Kürzel und Rollen: Die Suche geht über ALLE Unternehmen und liefert deshalb nur die
                  TATSACHE einer Beziehung und ihre Rollen, keine fremden Nummern, Stati oder Konditionen (P12). */}
              <span className="ka__dublette-chips">
                {t.relationShortCodes.map((kuerzel) => (
                  <Chip key={kuerzel} className="ka__chip" {...hausfarbeChip(p.companies, kuerzel)}>{kuerzel}</Chip>
                ))}
              </span>
              {/* Im aktiven Unternehmen schon Kunde oder Interessent: in seine Akte. Sonst „Rolle Kunde hinzufügen“ — an
                  der vorhandenen Beziehung (etwa als Lieferant) oder als neue Beziehung hier. Gehört die Firma woandershin,
                  bleibt daneben der Wechsel des aktiven Unternehmens (E361, R20). Hat die Maske schon Eingaben, fragt der
                  Verlassen-Wächter — beim Wechsel wie beim Sprung in die Akte; nur mit dem Suchnamen allein geht es in
                  beiden Fällen ohne Frage hinüber. */}
              {!schonKunde && aktiv !== undefined && (
                <KiKnopf className="ka__dublette-knopf" deaktiviert={arbeitet || hinzuLaeuft}
                  onClick={() => { void kundeHinzufuegen(t); }}>
                  {PARTNER_TEXT.addCustomerRole}
                </KiKnopf>
              )}
              {hier !== undefined ? (
                <KiKnopf className="ka__dublette-knopf" deaktiviert={arbeitet} onClick={() => zurAkteDesTreffers(t.id)}>
                  {PARTNER_TEXT.toRecord}
                </KiKnopf>
              ) : t.relationShortCodes.map((kuerzel) => {
                const firma = p.companies.find((c) => c.shortCode === kuerzel);
                return firma === undefined ? null : (
                  <KiKnopf key={kuerzel} className="ka__dublette-knopf" deaktiviert={arbeitet}
                    onClick={() => p.onWechseln(firma.id, t.id, { ungefragt: !weitereEingaben })}>
                    {PARTNER_TEXT.switchTo(kuerzel)}
                  </KiKnopf>
                );
              })}
            </li>
          );
        })}
      </ul>
    </section>
  );

  return (
    <GeschaeftspartnerDialog modus="anlegen" titel={PARTNER_TEXT.newPartner} client={p.client}
      draft={entwurf} setDraft={setEntwurf} base={EMPTY_DRAFT} fieldErrors={feldFehler}
      formularProps={formular.formularProps} gesperrt={formular.zustand !== "bereit"}
      // Mit dem Anlegen beginnt der innere Stand der Ortszeile neu (M-6a).
      ortszeileKey={angelegtId ?? "neu"}
      logo={{ chosen: gewaehltesLogo, onChosen: setGewaehltesLogo, existing: null }}
      nachFirmenname={dubletten}
      beziehung={(
        <>
          <h3>{PARTNER_TEXT.relationAt(aktiv?.name ?? PARTNER_TEXT.empty)}</h3>
          {/* Die Rollenwahl (C9, P2): Vorgabe aus den Chips der Liste, mindestens eine. */}
          <RoleChoiceField wert={rollen} onWert={setRollen} fehler={feldFehler.status ?? feldFehler.supplierStatus} />
          {mitKundenrolle && (
            <Feld id={partnerFieldId("customerNumber")} label={PARTNER_TEXT.customerNumber} wert={nummer} bearbeitbar schmal
              onChange={setNummer} fehler={feldFehler.customerNumber} hilfe={PARTNER_TEXT.customerNumberHintCreate} />
          )}
          {mitKundenrolle && (
            <Feld id={partnerFieldId("customerSince")} label={PARTNER_TEXT.customerSince} wert={seit} bearbeitbar typ="date"
              onChange={setSeit} fehler={feldFehler.customerSince} />
          )}
        </>
      )}
      // Die Meldungen stehen im Slot des Panels und rollen nicht mit dem Inhalt davon.
      {...(meldung === null && formular.meldung === null ? {} : { meldungen: (
        <>
          {meldung !== null && <Hinweis art="fehler">{meldung}</Hinweis>}
          {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}
        </>
      ) })}
      onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}
      fuss={(
        <>
          {angelegtId !== null ? (
            // Der Partner ist angelegt, nur das Logo scheiterte: kein zweites Anlegen (das erzeugte eine Dublette),
            // sondern der ausdrückliche Sprung in die Akte.
            <KiKnopf art="primaer" onClick={() => { fertig(angelegtId); }}>{PARTNER_TEXT.toRecord}</KiKnopf>
          ) : (
            <KiKnopf art="primaer" deaktiviert={arbeitet || !rolleGewaehlt} knopfRef={anlegenHinweis}
              onClick={() => { void anlegen(); }}>
              {treffer.length > 0 ? PARTNER_TEXT.createAnyway : PARTNER_TEXT.create}
            </KiKnopf>
          )}
          <KiKnopf deaktiviert={arbeitet} onClick={() => { void abbrechen(); }}>{PARTNER_TEXT.cancel}</KiKnopf>
        </>
      )} />
  );
}
